/**
 * Accounts on the client: who this browser is, and how the app learns it.
 *
 * The relay is the authority (§1) — `GET /api/auth/me` reports the mode, the
 * signed-in user, and whether Google is configured at all — but the app must
 * never WAIT for it. Offline-first means a browser that has signed in before
 * keeps working with no network at all, against the account it cached (D8), and
 * a browser that has never signed in opens as the anonymous owner.
 *
 * This module is the boot gate's whole world: the cached answer, the live
 * answer, the identity change a sign-in is — the anonymous owner's queued work
 * has to cross the boundary with it, or it never syncs again (I6) — and the two
 * entry points that change it (sign in, sign out), plus the one URL that starts
 * the flow. `main.ts` calls exactly one of these — `resolveAccount()` — before it
 * mounts anything.
 *
 * `Account.owner_id` is the server's `id` (D10: `users.id` IS the `owner_id`),
 * and the mapping happens once, here: the wire shape says `id`, the identity the
 * client builds says `owner_id`.
 */

import {
  currentDbOwner,
  deleteOtherOwnerDatabases,
  getMetaForOwner,
  setDbOwner,
  setMetaForOwner,
} from './db';
import { uuidv7 } from './identity';
import {
  ANONYMOUS_OWNER_ID,
  fetchOwnerNotebooks,
  getLastNotebookId,
  getOrCreateDeviceId,
  RelayUnauthorizedError,
  storeFor,
} from './operations';

/** One account, in the client's own vocabulary. */
export interface Account {
  /** The account's id — also the `owner_id` every one of its notebooks carries (D10). */
  owner_id: string;
  email: string;
  name: string | null;
  is_admin: boolean;
}

/** The relay's mode and who this browser is on it. */
export interface AuthState {
  mode: 'anonymous' | 'account';
  /** The signed-in account, or `null` — anonymous mode, or account mode signed out. */
  user: Account | null;
  /** Whether Google credentials are configured at all (D2). */
  signin_available: boolean;
}

/**
 * What the boot gate resolved (R7): the owner this browser is acting as, this
 * browser's device id, and the relay's answer.
 *
 * Deliberately not an `Identity`: there is no `notebook_id` before a notebook
 * has been chosen, and the container — which does the choosing — composes one
 * from these two ids.
 */
export interface ResolvedAccount {
  owner_id: string;
  device_id: string;
  auth: AuthState;
}

/**
 * `/api/auth/me`'s wire shape (`routes/auth.rs`).
 *
 * The account arrives as `id` there because it is the same serialisation the
 * admin routes serve (`db::accounts::Account`); nothing downstream of this file
 * should ever see that spelling.
 */
interface WireAuthState {
  mode: 'anonymous' | 'account';
  user: { id: string; email: string; name: string | null; is_admin: boolean } | null;
  signin_available: boolean;
}

/** The `meta` key the resolved account is cached under (§5). */
const ACCOUNT_KEY = 'account';

/**
 * The acting owner an auth state names: the account's id, or the anonymous
 * owner (the one unclaimed, pre-account owner, design §3).
 */
function ownerOf(auth: AuthState): string {
  return auth.user?.owner_id ?? ANONYMOUS_OWNER_ID;
}

/**
 * Where the account cache lives, and why nothing here moves the acting owner.
 *
 * The cache must be readable at the NEXT boot, and the next boot reads `db()`
 * before it knows anything: `dbOwner` starts at the anonymous owner (`db.ts`),
 * and the cache is what tells the gate whose store to switch to. So the cache
 * lives in the anonymous owner's store, and every read or write of it NAMES that
 * store (`getMetaForOwner` / `setMetaForOwner`).
 *
 * Naming it, rather than pinning it with `setDbOwner`, is the load-bearing half.
 * The re-validation below runs while the app is live, so the acting owner is a
 * real account's while the write lands; `setDbOwner` bumps the DB generation, so
 * a pin that moved the owner away and back would leave every store opened in the
 * window `#stale()` for the rest of the page's life — no drain, no session
 * writes, a notebook that never syncs again. An owner-keyed write opens a
 * transient handle for that store and touches nobody's generation.
 */

/**
 * The cached account, or `null` when this browser has never signed in.
 *
 * A cleared cache is a `null` VALUE rather than a deleted row: `db.ts` exposes
 * no delete for a single key, and the two are indistinguishable to every reader
 * here (`getMetaForOwner` answers `undefined` for a missing row and `null` for
 * this one).
 */
async function readCachedAccount(): Promise<AuthState | null> {
  return (await getMetaForOwner<AuthState | null>(ANONYMOUS_OWNER_ID, ACCOUNT_KEY)) ?? null;
}

/**
 * Persists the account this browser is acting as, so the NEXT boot knows it with
 * no network (§5, D8).
 *
 * Only a SIGNED-IN state is cached, and an anonymous one clears the row: the
 * cache's whole job is "which account's store do I open", and an anonymous relay
 * has no answer to give — caching it would buy nothing (the offline boot makes
 * the same decision with no cache at all) while pinning a `signin_available`
 * that the relay may have flipped since.
 */
export async function cacheAccount(auth: AuthState): Promise<void> {
  if (!auth.user) return forgetAccount();
  await setMetaForOwner(ANONYMOUS_OWNER_ID, ACCOUNT_KEY, auth);
}

/** Forgets the cached account — sign-out, and a login the relay has refused. */
export async function forgetAccount(): Promise<void> {
  await setMetaForOwner(ANONYMOUS_OWNER_ID, ACCOUNT_KEY, null);
}

/**
 * `GET /api/auth/me`: the relay's answer about the mode and this browser.
 *
 * A `401` is deliberately NOT an error case to swallow: it is the relay saying
 * "account mode, and you have no live login", which is what puts the sign-in
 * view up. It is raised as `RelayUnauthorizedError` — the same type the relay
 * transport uses (§4) — so callers can tell it apart from "no answer at all".
 */
export async function fetchAuthState(): Promise<AuthState> {
  const response = await fetch('/api/auth/me');
  if (response.status === 401) throw new RelayUnauthorizedError('me: 401');
  if (!response.ok) throw new Error(`me: ${response.status}`);
  const body = (await response.json()) as WireAuthState;
  return {
    mode: body.mode,
    user: body.user && {
      owner_id: body.user.id,
      email: body.user.email,
      name: body.user.name,
      is_admin: body.user.is_admin,
    },
    signin_available: body.signin_available,
  };
}

/**
 * The boot gate (§5): who this browser is, and what the app should show.
 *
 * Precedence, in order:
 *
 *   1. a cached account → proceed IMMEDIATELY with it, re-validating in the
 *      background. Offline works: the cache IS the identity (D8).
 *   2. no cache, and the relay answers → the answer decides: a signed-in account
 *      is cached and used; a `401` is account mode with no live login, which is
 *      the sign-in view; anonymous proceeds as the anonymous owner.
 *   3. no cache, and no answer (offline, or a 5xx) → proceed as the anonymous
 *      owner, and cache NOTHING. There is nothing to leak — no account is being
 *      acted as — and the relay refuses every request this app then makes, so the
 *      worst case is a local-only app that reconciles on the next boot. An
 *      unreachable relay is no evidence about the mode, and inventing
 *      `signin_available` from it would offer a door nobody has checked.
 *
 * The acting owner is switched as the LAST act: everything the gate itself reads
 * (the cache, the device id) belongs to the store the browser opens with, while
 * the container mounting next must find the account's notebooks — whose store is
 * named by the owner this returns.
 *
 * Which is not the same act in both paths. Case 1 is a plain owner switch: the
 * browser was already this account. Case 2 is an IDENTITY CHANGE — the browser
 * has been acting as the anonymous owner until this instant — and it goes
 * through `adoptAccount`, because the anonymous owner's queued work has to cross
 * the boundary the change creates or it never syncs again (R18).
 */
export async function resolveAccount(): Promise<ResolvedAccount> {
  const device_id = await getOrCreateDeviceId();
  const cached = await readCachedAccount();

  if (cached) {
    // Fire and forget on purpose: the relay gets the last word, but it never
    // holds up the boot.
    void revalidate();
    const owner_id = ownerOf(cached);
    // A plain owner switch, and deliberately not `adoptAccount` below: a cache
    // only exists because a boot already resolved this account live — which is
    // when the anonymous outbox crossed the boundary (R18) — and while the cache
    // stands this browser never acts anonymously, so nothing of the anonymous
    // owner's can have been queued since. There is therefore nothing left to
    // carry, and this is the OFFLINE path: it must touch no network at all.
    await setDbOwner(owner_id);
    return { owner_id, device_id, auth: cached };
  }

  const auth = await fetchAuthState().catch((error: unknown): AuthState => {
    if (error instanceof RelayUnauthorizedError) {
      // Account mode with no live login. `signin_available` is `true` because a
      // relay that HAS an account refuses to start without credentials
      // (`validate_startup`, §3): the 401 carries no body to read it from, and a
      // relay running in this state has sign-in configured by construction.
      return { mode: 'account', user: null, signin_available: true };
    }
    return { mode: 'anonymous', user: null, signin_available: false };
  });

  await cacheAccount(auth);
  const owner_id = ownerOf(auth);
  if (owner_id === ANONYMOUS_OWNER_ID) {
    // Nothing changed owner: this browser acts as the anonymous owner, and the
    // store it opened with is that owner's. No boundary to cross, nothing to
    // carry.
    await setDbOwner(ANONYMOUS_OWNER_ID);
  } else {
    // The identity changed under this browser, and this is the boot that finds
    // out: a sign-in (the flow is a full-page navigation, so the account is
    // resolved HERE), or a first-visit offline browser reconciling with the
    // account the relay turns out to have. Until this instant the browser has
    // been acting as the anonymous owner, so the change is an identity change —
    // `setIdentity`, never `setDbOwner` before it (R18).
    await adoptAccount(owner_id, device_id);
  }
  return { owner_id, device_id, auth };
}

/**
 * Adopts an account this boot resolved live, carrying the anonymous owner's
 * queued work across the boundary the change creates (R18, design §5).
 *
 * `owner_id` is baked into every outbox envelope at `authorise`, and the local
 * cache is per-owner, so the identity change is BOTH a re-stamp and a database
 * move: work queued before the sign-in is stamped with the owner the relay has
 * just stopped accepting it from, and it lives in a store this browser now reads
 * as someone else's. Left where it is, it never syncs again — "offline-authored
 * work silently stops existing" (I6), which is the one thing a local-first
 * client must never do.
 *
 * The move is `setIdentity`'s job, and it is called ALONE. The primitive reads
 * the pending rows out of the store this browser is acting as, switches the
 * acting owner, and writes the survivors into the new owner's store; calling
 * `setDbOwner` first would move the read boundary out from under the rows the
 * move exists to carry, and they would be stranded (R18).
 *
 * `setIdentity` is a store method, so the call needs the store this browser was
 * ACTING AS: the anonymous owner's. Its notebook is the one the browser was last
 * in — the subject the change is about — or a freshly minted id when it was
 * never in one; the move itself is whole-store and never reads that id, and the
 * store is dropped as soon as the change is done.
 *
 * What travels is decided by the new owner's notebook list, read first: work
 * queued for a notebook the account does not have is not this person's to
 * transmit (design §5, Review Focus 3). Carrying it is not merely wrong — a
 * shared browser would hand the previous occupant's notebooks to whoever signed
 * in next — it also jams the drain, which stops at the first op the relay will
 * not commit.
 */
async function adoptAccount(owner_id: string, device_id: string): Promise<void> {
  // The subject of the change, and the list that decides what travels with it.
  // The list has to be read BEFORE the switch: every list the client can ask for
  // answers for the owner this browser is acting as, and until `setIdentity` runs
  // that owner is still the anonymous one.
  const notebook_id = (await getLastNotebookId()) ?? uuidv7();
  const knownNotebookIds = await newOwnerNotebooks(owner_id);

  const acting = await storeFor({
    owner_id: ANONYMOUS_OWNER_ID,
    device_id,
    notebook_id,
  });
  // ALONE (R18): the primitive reads the queued rows out of the store this
  // browser was acting as, switches the acting owner, and writes the survivors
  // into the new owner's store. A `setDbOwner` first would move the read
  // boundary out from under the rows the move exists to carry.
  await acting.setIdentity({ owner_id, device_id, notebook_id }, knownNotebookIds);
}

/**
 * The new owner's notebook list — "the post-sign-in refresh" (R18).
 *
 * Read straight off the owner-scoped route, because every list primitive the
 * engine already has answers for the owner this browser is ACTING as, and the
 * whole point of this call is that the browser does not act as the account yet.
 * It deliberately does not touch the local list cache either: that cache belongs
 * to the store the boot is about to open, and the container's own refresh writes
 * it.
 *
 * `undefined` when the list cannot be read — offline, or any refusal — and the
 * carry then keeps everything. The failure directions are not symmetric: an
 * unknown list treated as an empty one would DROP every queued op and destroy
 * offline-authored work (I6), while carrying an op for a notebook this account
 * does not have is refused by the relay and never lost.
 */
async function newOwnerNotebooks(owner_id: string): Promise<Set<string> | undefined> {
  try {
    const { notebooks } = await fetchOwnerNotebooks(owner_id);
    return new Set(notebooks.map((n) => n.notebook_id));
  } catch {
    return undefined;
  }
}

/**
 * Re-validates the cached account in the background (§5).
 *
 * A refusal is the one answer that invalidates the cache — the login is gone, so
 * the NEXT boot must land on the sign-in view rather than repeat this one — and
 * it is cleared. Everything else (an unreachable relay, a 5xx) leaves the cache
 * ALONE: a network blip must not erase who this browser is (D8). The running app
 * keeps the identity it booted with either way; an expired login on a live drain
 * surfaces through `authRequired` (operations.ts), which is the path that owns it
 * and the signal the mounted app reads (R19, `NotebookContainer`).
 *
 * Both the re-validation's write and its clear name the boot store rather than
 * pinning it (see the cache-location note above): this runs AFTER the mount, with
 * live stores on the account, and moving the acting owner here would stale them
 * permanently.
 */
async function revalidate(): Promise<void> {
  try {
    await cacheAccount(await fetchAuthState());
  } catch (error) {
    if (!(error instanceof RelayUnauthorizedError)) return;
    // Nobody awaits this function, so the clear must not throw on its own.
    await forgetAccount().catch(() => {});
  }
}

/**
 * The URL that starts the Google flow (`GET /api/auth/signin`, §3), carrying the
 * invite code when there is one.
 *
 * The code rides the query string because that is the surface the relay
 * specifies; it never reaches Google and never enters a cookie (R12 — the server
 * keeps it beside the nonce). A blank code is no code: `?invite=` would be an
 * empty parameter for the relay to filter.
 */
export function signInUrl(invite?: string): string {
  const code = invite?.trim();
  return code ? `/api/auth/signin?invite=${encodeURIComponent(code)}` : '/api/auth/signin';
}

/**
 * Ends the login: revoke it on the relay, forget it here, and go back to acting
 * as nobody.
 *
 * The POST is best-effort. An offline browser cannot reach the relay, and the
 * local half is the half this client owns — staying signed in to a dead relay is
 * not a service to anyone. Nothing about a notebook moves: the local store is
 * scoped by owner (§5), so this person's cached log and any unsent work in it stay
 * exactly where they are for their next sign-in, and the next person to sign in
 * opens their own empty one.
 *
 * Which owner's store survives is the whole of the cleanup, so it is read FIRST
 * — before anything below can move the acting owner. Then, once this browser is
 * nobody again, every OTHER owner's store on it is collected
 * (`deleteOtherOwnerDatabases`): per-owner scoping protects the next account from
 * the previous one, but the shared anonymous store can still hold an earlier
 * occupant's notebooks, and an offline boot acts as exactly that owner
 * (`resolveAccount`). Sign-out is where those leftovers are cleared, or the next
 * person on this browser reads them.
 */
export async function signOut(): Promise<void> {
  const leaving = currentDbOwner();
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // Offline: there is nothing to revoke from here.
  }
  // The pre-login store first, so the cache is cleared where it actually lives
  // and a still-running app stops writing into the signed-out person's store.
  await setDbOwner(ANONYMOUS_OWNER_ID);
  await forgetAccount();
  await deleteOtherOwnerDatabases(leaving);
}

/** Deletes the signed-in account and everything attached to it: every notebook
 * it owns, the login tokens, the OAuth identities, and the invite codes
 * (R5.5). The relay answers `DELETE /api/auth/me` — the live login IS the
 * proof of whose account it is, and the account it deletes is always the
 * caller's own.
 *
 * The typed-confirm and export-first safeguards are the client's, in front of
 * this call. The caller signs the browser out afterwards (the container's
 * sign-out sequence), and surfaces any failure itself. */
export async function deleteAccount(): Promise<void> {
  const response = await fetch('/api/auth/me', { method: 'DELETE' });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `delete account: ${response.status}`);
  }
}
